import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, Edit2, Trash2, Play, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const Sprints = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sprints, setSprints] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showSprintModal, setShowSprintModal] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [sprintForm, setSprintForm] = useState({
    name: '',
    goal: '',
    start_date: '',
    end_date: '',
    status: 'planned'
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchSprints();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error(t('projectsLoadFailed'));
    }
  };

  const fetchSprints = async () => {
    if (!selectedProject) return;
    
    setLoading(true);
    try {
      const [sprintsResponse, tasksResponse] = await Promise.all([
        axiosInstance.get(`/projects/${selectedProject}/sprints`),
        axiosInstance.get(`/projects/${selectedProject}/tasks`)
      ]);
      
      // Add task count to each sprint
      const sprintsWithTaskCount = sprintsResponse.data.map(sprint => {
        const taskCount = tasksResponse.data.filter(task => task.sprint_id === sprint.id).length;
        return { ...sprint, taskCount };
      });
      
      setSprints(sprintsWithTaskCount);
    } catch (error) {
      toast.error(t('sprintsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSprint = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!sprintForm.name || !sprintForm.start_date || !sprintForm.end_date) {
      toast.error(t('fillRequiredFields'));
      return;
    }
    
    if (new Date(sprintForm.end_date) <= new Date(sprintForm.start_date)) {
      toast.error(t('endDateAfterStart'));
      return;
    }
    
    try {
      if (editingSprint) {
        await axiosInstance.put(`/sprints/${editingSprint.id}`, sprintForm);
        toast.success(t('sprintUpdated'));
      } else {
        await axiosInstance.post(`/projects/${selectedProject}/sprints`, sprintForm);
        toast.success(t('sprintCreated'));
      }
      
      fetchSprints();
      setShowSprintModal(false);
      resetForm();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Sprint işlemi başarısız';
      toast.error(errorMsg);
      console.error('Sprint error:', error.response?.data);
    }
  };

  const handleDeleteSprint = async (sprintId) => {
    if (!window.confirm(t('confirmDeleteSprint'))) return;
    
    try {
      await axiosInstance.delete(`/sprints/${sprintId}`);
      toast.success(t('sprintDeleted'));
      fetchSprints();
    } catch (error) {
      toast.error(t('sprintDeleteFailed'));
    }
  };

  const handleStatusChange = async (sprint, newStatus) => {
    try {
      await axiosInstance.put(`/sprints/${sprint.id}`, { status: newStatus });
      toast.success(newStatus === 'active' ? t('sprintStartedEmailSent') : t('sprintStatusUpdated'));
      fetchSprints();
    } catch (error) {
      toast.error(t('statusUpdateFailed'));
    }
  };

  const resetForm = () => {
    setSprintForm({
      name: '',
      goal: '',
      start_date: '',
      end_date: '',
      status: 'planned'
    });
    setEditingSprint(null);
  };

  const openEditModal = (sprint) => {
    setEditingSprint(sprint);
    setSprintForm({
      name: sprint.name,
      goal: sprint.goal || '',
      start_date: sprint.start_date.split('T')[0],
      end_date: sprint.end_date.split('T')[0],
      status: sprint.status
    });
    setShowSprintModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      planned: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      planned: 'Planlandı',
      active: 'Aktif',
      completed: 'Tamamlandı',
      cancelled: 'İptal Edildi'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('backToDashboard')}
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('sprintManagement')}</h1>
              <p className="text-gray-600 mt-1">{t('manageProjectSprints')}</p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowSprintModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus size={20} />
            {t('newSprint')}
          </button>
        </div>

        {/* Project Selector */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('selectProject')}
          </label>
          <select
            value={selectedProject || ''}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          >
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sprints Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : sprints.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noSprintsYet')}</h3>
            <p className="text-gray-600">{t('createFirstSprint')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sprints.map(sprint => (
              <div key={sprint.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{sprint.name}</h3>
                  {getStatusBadge(sprint.status)}
                </div>
                
                {sprint.goal && (
                  <p className="text-gray-600 text-sm mb-4">{sprint.goal}</p>
                )}
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar size={16} className="mr-2" />
                    {new Date(sprint.start_date).toLocaleDateString('tr-TR')} - {new Date(sprint.end_date).toLocaleDateString('tr-TR')}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <CheckCircle size={16} className="mr-2" />
                    {sprint.taskCount || 0} {t('tasks')}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  {sprint.status === 'planned' && (
                    <button
                      onClick={() => handleStatusChange(sprint, 'active')}
                      className="flex-1 bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 flex items-center justify-center gap-1"
                    >
                      <Play size={16} />
                      Başlat
                    </button>
                  )}
                  
                  {sprint.status === 'active' && (
                    <button
                      onClick={() => handleStatusChange(sprint, 'completed')}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 flex items-center justify-center gap-1"
                    >
                      <CheckCircle size={16} />
                      Tamamla
                    </button>
                  )}
                  
                  <button
                    onClick={() => openEditModal(sprint)}
                    className="bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200"
                  >
                    <Edit2 size={16} />
                  </button>
                  
                  <button
                    onClick={() => handleDeleteSprint(sprint.id)}
                    className="bg-red-100 text-red-700 px-3 py-2 rounded text-sm hover:bg-red-200"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sprint Modal */}
      {showSprintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">
              {editingSprint ? t('editSprint') : t('newSprint')}
            </h2>
            
            <form onSubmit={handleCreateSprint}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sprintName')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={sprintForm.name}
                    onChange={(e) => setSprintForm({...sprintForm, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Sprint 1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('sprintGoal')}
                  </label>
                  <textarea
                    value={sprintForm.goal}
                    onChange={(e) => setSprintForm({...sprintForm, goal: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder={t('writeSprintGoal')}
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('startDate')} *
                    </label>
                    <input
                      type="date"
                      required
                      value={sprintForm.start_date}
                      onChange={(e) => setSprintForm({...sprintForm, start_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('endDate')} *
                    </label>
                    <input
                      type="date"
                      required
                      value={sprintForm.end_date}
                      onChange={(e) => setSprintForm({...sprintForm, end_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durum
                  </label>
                  <select
                    value={sprintForm.status}
                    onChange={(e) => setSprintForm({...sprintForm, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="planned">Planlandı</option>
                    <option value="active">Aktif</option>
                    <option value="completed">Tamamlandı</option>
                    <option value="cancelled">İptal Edildi</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowSprintModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  {editingSprint ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sprints;
